import { useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { firstInvalidControl } from '@/lib/i18n/nativeValidation';
import { industries } from '@/lib/schemas/brandKit';
import { uploadImage } from '@/lib/images/upload';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { XkBrand } from '@/lib/api/types';

/**
 * Crear una marca: nombre, industria y logotipo.
 *
 * La **industria se elige de una lista**, no se escribe. Con campo libre acaban
 * conviviendo «F&B», «Food and Beverage» y «bebidas» como si fueran tres cosas,
 * y agrupar marcas por industria —lo único para lo que sirve el dato— deja de
 * funcionar. La lista vive en `@/lib/schemas/brandKit`, compartida con el resto
 * de la aplicación.
 *
 * El **logotipo se sube aquí** porque es lo que identifica a la marca en el
 * conmutador, en la barra y en cada pantalla del producto: una marca recién
 * creada sin logotipo es una inicial gris hasta que alguien se acuerda de
 * entrar en Brand Studio. Sigue siendo opcional — quien no tenga el archivo a
 * mano entra igual, y el resto de la identidad (paleta, tipografías) se afina
 * después en Brand Studio.
 *
 * The backend (`POST /co-companies`) auto-sets `parentId`/`companyId` to the
 * caller's agency and auto-creates the `co_company_accesses` row with
 * `xotskiAccess=1`, so a single call makes the brand show up in the switcher.
 * Plan caps are enforced server-side via `@PlanGated('workspace','brand')`;
 * the caller should gate the entry point on `usePlanQuota('maxBrands')` and
 * pass `onQuotaExceeded` to pivot to the upgrade prompt on a late rejection.
 */
export function NewBrandDialog({
  open,
  onOpenChange,
  onCreated,
  onQuotaExceeded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (brand: XkBrand) => void;
  onQuotaExceeded?: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [logo, setLogo] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const ficheroRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setName('');
    setIndustry('');
    setLogo('');
    setSubiendo(false);
  };

  /*
   * Se sube al servidor y se guarda la RUTA, no la imagen.
   *
   * `co_companies.logo` es un varchar(255): un data URI en base64 lo desborda
   * en el primer intento («Data too long for column 'logo'»). Lo que se guarda
   * es el `/uploads/<archivo>` que devuelve la subida.
   */
  const elegirLogo = async (file: File) => {
    setSubiendo(true);
    try {
      const res = await uploadImage(file, { preset: 'logo' });
      setLogo(res.url);
    } catch (e) {
      toast({
        tone: 'error',
        title: t('brand.new.logoFailed'),
        description: (e as Error).message,
      });
    } finally {
      setSubiendo(false);
      if (ficheroRef.current) ficheroRef.current.value = '';
    }
  };

  const create = useMutation({
    mutationFn: () =>
      brandsApi.create({
        name: name.trim(),
        industry: industry || undefined,
        logo: logo || undefined,
      }),
    onSuccess: (brand) => {
      // Optimistically splice the new brand into the cached list BEFORE the
      // caller selects it. OnboardingGate has a "repair stale selection"
      // effect that resets the selection to brands[0] when the selected id
      // isn't in the list yet — without this, selecting the freshly-created
      // brand would be clobbered until the background refetch lands. Writing
      // the cache synchronously makes the id valid in the same render.
      queryClient.setQueryData<{ items: XkBrand[]; total?: number }>(
        queryKeys.brands.all,
        (old) => {
          if (!old) return old;
          if (old.items?.some((b) => b.id === brand.id)) return old;
          return {
            ...old,
            items: [...(old.items ?? []), brand],
            total: (old.total ?? old.items?.length ?? 0) + 1,
          };
        },
      );
      // Reconcile with the server (count, ordering, derived fields).
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('brand.new.created', { name: brand.name ?? name.trim() }) });
      reset();
      onCreated(brand);
    },
    onError: (e: Error) => {
      // Plan cap rejected server-side — pivot to the upgrade prompt instead of
      // a raw error toast. PlanLimitException is HTTP 409 with
      // `code: 'PLAN_LIMIT_EXCEEDED'`; 409 on this endpoint always means the
      // brand cap, so treat the status as a fallback discriminator.
      const apiErr = e as { status?: number; payload?: { code?: string } };
      const isPlanLimit =
        apiErr.payload?.code === 'PLAN_LIMIT_EXCEEDED' ||
        apiErr.payload?.code === 'PLAN_LIMIT_REACHED' ||
        apiErr.status === 409;
      if (isPlanLimit && onQuotaExceeded) {
        onOpenChange(false);
        onQuotaExceeded();
        return;
      }
      toast({ tone: 'error', title: t('brand.new.failed'), description: e.message });
    },
  });

  // Mientras sube el logotipo no se envía: la mutación leería `logo` vacío y
  // la marca nacería sin él, que es justo lo que se acaba de pedir.
  const canSubmit = name.trim().length >= 2 && !create.isPending && !subiendo;
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const invalido = firstInvalidControl(e.currentTarget, t);
    if (invalido) {
      toast({ tone: 'error', title: invalido.message });
      return;
    }
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('brand.new.title')}</DialogTitle>
          <DialogDescription>{t('brand.new.subtitle')}</DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('brand.new.nameLabel')} <span className="text-[var(--color-accent-ink)]">*</span>
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('brand.new.namePlaceholder')}
              autoFocus
              required
              minLength={2}
              className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('brand.new.industryLabel')}
            </div>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] focus:border-[var(--color-accent-ink)] focus:outline-none"
            >
              <option value="">{t('brand.new.industryPick')}</option>
              {industries.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>

          <div className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('brand.new.logoLabel')}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-xl border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]">
                {logo ? (
                  <img
                    src={resolveImageUrl(logo)}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <ImagePlus className="h-5 w-5 text-[var(--color-xk-text-muted)]" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => ficheroRef.current?.click()}
                    disabled={subiendo}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)] disabled:opacity-50"
                  >
                    {subiendo ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t('brand.new.logoUploading')}
                      </>
                    ) : (
                      t(logo ? 'brand.new.logoReplace' : 'brand.new.logoPick')
                    )}
                  </button>
                  {logo && !subiendo && (
                    <button
                      type="button"
                      onClick={() => setLogo('')}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
                    >
                      <X className="h-3 w-3" />
                      {t('common.remove')}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-[var(--color-xk-text-muted)]">
                  {t('brand.new.logoHint')}
                </p>
              </div>
              <input
                ref={ficheroRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void elegirLogo(f);
                }}
              />
            </div>
          </div>
          <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-muted)]">
            {t('brand.new.studioHint')}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('common.cancel')}
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {create.isPending ? t('brand.new.creating') : t('brand.new.submit')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
